export type PlanningBoardOpenSlotMatch = {
  id: string;
  league_configuration_id: string | null;
};

export type PlanningBoardOpenSlotPosition = {
  id: string;
  league_configuration_id: string | null;
  position_code: string | null;
  line_type?: string | null;
  display_order?: number | string | null;
  required_player_count?: number | string | null;
  required_male_count?: number | string | null;
  required_female_count?: number | string | null;
};

export type PlanningBoardOpenSlotEntry = {
  match_event_id: string;
  league_configuration_position_id: string;
  slot_number?: number | string | null;
  player_id?: string | null;
};

export type PlanningBoardLineStatus = 'normal' | 'default_by_opponent' | 'default_by_us' | string | null | undefined;

export type PlanningBoardOpenSlotsIndicator =
  | {
      show: false;
      label: null;
      tooltip: null;
      missingSlots: [];
      isUnstarted: false;
    }
  | {
      show: true;
      label: string;
      tooltip: string;
      missingSlots: string[];
      isUnstarted: boolean;
    };

const completeIndicator: PlanningBoardOpenSlotsIndicator = {
  show: false,
  label: null,
  tooltip: null,
  missingSlots: [],
  isUnstarted: false,
};

const defaultedLineStatuses = new Set(['default_by_opponent', 'default_by_us']);

function numericValue(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function requiredPlayerCount(position: PlanningBoardOpenSlotPosition): number {
  const parsed = numericValue(position.required_player_count);
  return parsed && parsed > 0 ? Math.floor(parsed) : 1;
}

function displayOrder(position: PlanningBoardOpenSlotPosition): number {
  const parsed = numericValue(position.display_order);
  return parsed === null ? Number.MAX_SAFE_INTEGER : parsed;
}

function positionCode(position: PlanningBoardOpenSlotPosition): string {
  return position.position_code || 'Line';
}

/**
 * Mixed lineup slots use the same canonical convention as the Lineup Builder:
 * slot 1 is the women's (left) player and slot 2 is the men's (right) player.
 * This intentionally uses configured position requirements, never player gender.
 */
function isCanonicalMixedDoublesPosition(position: PlanningBoardOpenSlotPosition): boolean {
  return String(position.line_type || '').toLowerCase() === 'mixed_doubles'
    && requiredPlayerCount(position) === 2
    && numericValue(position.required_female_count) === 1
    && numericValue(position.required_male_count) === 1;
}

function filledSlotNumbers(entries: PlanningBoardOpenSlotEntry[], requiredCount: number): Set<number> {
  const filled = new Set<number>();
  entries.forEach((entry) => {
    if (!entry.player_id) return;
    const slotNumber = numericValue(entry.slot_number);
    if (slotNumber && slotNumber >= 1 && slotNumber <= requiredCount) filled.add(Math.floor(slotNumber));
  });
  return filled;
}

function assignedCountForPosition(entries: PlanningBoardOpenSlotEntry[], requiredCount: number): number {
  const filledSlotNumbers = new Set<number>();
  let unslottedAssignments = 0;

  entries.forEach((entry) => {
    if (!entry.player_id) return;
    const slotNumber = numericValue(entry.slot_number);
    if (slotNumber && slotNumber >= 1 && slotNumber <= requiredCount) {
      filledSlotNumbers.add(Math.floor(slotNumber));
    } else {
      unslottedAssignments += 1;
    }
  });

  return Math.min(requiredCount, filledSlotNumbers.size + unslottedAssignments);
}

export function buildPlanningBoardOpenSlotsIndicator({
  match,
  positions,
  entries,
  lineStatusesByPositionId = new Map(),
}: {
  match: PlanningBoardOpenSlotMatch;
  positions: PlanningBoardOpenSlotPosition[];
  entries: PlanningBoardOpenSlotEntry[];
  lineStatusesByPositionId?: Map<string, PlanningBoardLineStatus>;
}): PlanningBoardOpenSlotsIndicator {
  const playablePositions = positions
    .filter((position) => position.league_configuration_id === match.league_configuration_id)
    .filter((position) => !defaultedLineStatuses.has(String(lineStatusesByPositionId.get(position.id) || 'normal')))
    .sort((a, b) => displayOrder(a) - displayOrder(b) || positionCode(a).localeCompare(positionCode(b), undefined, { numeric: true }));

  if (playablePositions.length === 0) return completeIndicator;

  const matchEntries = entries.filter((entry) => entry.match_event_id === match.id);
  const genericMissingSlots: Array<{ label: string; order: number }> = [];
  const womenMissingSlots: Array<{ label: string; order: number }> = [];
  const menMissingSlots: Array<{ label: string; order: number }> = [];
  playablePositions.forEach((position) => {
    const requiredCount = requiredPlayerCount(position);
    const positionEntries = matchEntries.filter((entry) => entry.league_configuration_position_id === position.id);
    if (isCanonicalMixedDoublesPosition(position)) {
      const filled = filledSlotNumbers(positionEntries, requiredCount);
      if (!filled.has(1)) womenMissingSlots.push({ label: `W-${positionCode(position)}`, order: displayOrder(position) });
      if (!filled.has(2)) menMissingSlots.push({ label: `M-${positionCode(position)}`, order: displayOrder(position) });
      return;
    }
    const missingCount = Math.max(0, requiredCount - assignedCountForPosition(positionEntries, requiredCount));
    Array.from({ length: missingCount }, () => genericMissingSlots.push({ label: positionCode(position), order: displayOrder(position) }));
  });
  const orderedByLine = (left: { order: number }, right: { order: number }) => left.order - right.order;
  const missingSlots = [
    ...womenMissingSlots.sort(orderedByLine),
    ...menMissingSlots.sort(orderedByLine),
    ...genericMissingSlots.sort(orderedByLine),
  ].map((slot) => slot.label);

  if (missingSlots.length === 0) return completeIndicator;

  const playablePositionIds = new Set(playablePositions.map((position) => position.id));
  const assignedPlayableCount = matchEntries.filter((entry) => entry.player_id && playablePositionIds.has(entry.league_configuration_position_id)).length;
  const isUnstarted = assignedPlayableCount === 0;

  if (isUnstarted) {
    return {
      show: true,
      label: 'Open: All',
      tooltip: 'Lineup has not been started.',
      missingSlots,
      isUnstarted: true,
    };
  }

  return {
    show: true,
    label: `Open: ${missingSlots.join(' · ')}`,
    tooltip: missingSlots.length === 1
      ? `1 lineup position still needs a player: ${missingSlots.join(', ')}.`
      : `${missingSlots.length} lineup positions still need players: ${missingSlots.join(', ')}.`,
    missingSlots,
    isUnstarted: false,
  };
}
